import {
  Card,
  Col,
  Empty,
  Input,
  List,
  Row,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlayCircle,
  faBookOpen,
  faChalkboardUser,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  getTrainings,
  getWellbeingMaterials,
} from "../repository/data.repository";
import { getProblemMessage, retry } from "../utils/http";
import { MAX_PAGE_SIZE } from "../repository/data.default";

// PD-02 §7.2 — Well-Being Corner. Three surfaces: Modules & Library (video
// / article content), Trainings (upcoming + past sessions), and a search bar
// across both. Company user browses what is available; employee assignment
// is routed through the dedicated actions.
function WellBeingPage() {
  return (
    <Col className="flex flex-auto flex-col overflow-hidden overflow-y-auto p-6">
      <Row className="mb-4 flex-row items-center">
        <Typography.Title level={3} className="!m-0 flex-auto">
          Well-Being Corner
        </Typography.Title>
      </Row>
      <Tabs
        defaultActiveKey="library"
        items={[
          {
            key: "library",
            label: (
              <Space>
                <FontAwesomeIcon icon={faBookOpen} />
                <span>Modules & Library</span>
              </Space>
            ),
            children: <MaterialsPanel />,
          },
          {
            key: "trainings",
            label: (
              <Space>
                <FontAwesomeIcon icon={faChalkboardUser} />
                <span>Trainings</span>
              </Space>
            ),
            children: <TrainingsPanel />,
          },
        ]}
      />
    </Col>
  );
}

function MaterialsPanel() {
  const [materials, setMaterials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    retry(() =>
      getWellbeingMaterials({
        searchQuery: { conditions: [] },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then((d) => setMaterials(d.data ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load materials."),
      )
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return materials;
    const q = query.toLowerCase();
    return materials.filter(
      (m) =>
        (m.name ?? "").toLowerCase().includes(q) ||
        (m.description ?? "").toLowerCase().includes(q) ||
        (m.categoryName ?? "").toLowerCase().includes(q),
    );
  }, [materials, query]);

  return (
    <Col className="flex flex-col gap-4">
      <Input
        allowClear
        placeholder="Search modules, articles, videos…"
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : filtered.length === 0 ? (
        <Empty description="No materials match your search." />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((m) => (
            <Col key={m.uid} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                size="small"
                title={m.name}
                extra={m.categoryName && <Tag color="cyan">{m.categoryName}</Tag>}
              >
                {m.description && (
                  <Typography.Paragraph
                    ellipsis={{ rows: 3 }}
                    className="!mb-2"
                  >
                    {m.description}
                  </Typography.Paragraph>
                )}
                <Space wrap size={6}>
                  {m.type && <Tag>{m.type}</Tag>}
                  {typeof m.duration === "number" && m.duration > 0 && (
                    <Tag icon={<FontAwesomeIcon icon={faPlayCircle} />}>
                      {Math.round(m.duration / 60)} min
                    </Tag>
                  )}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Col>
  );
}

function TrainingsPanel() {
  const [trainings, setTrainings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    retry(() =>
      getTrainings({
        searchQuery: { conditions: [] },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then((d) => setTrainings(d.data ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load trainings."),
      )
      .finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const upcoming = trainings.filter((t) => new Date(t.startDate).getTime() > now);
  const past = trainings.filter((t) => new Date(t.startDate).getTime() <= now);

  return (
    <Col className="flex flex-col gap-4">
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Typography.Title level={5} className="!m-0">
            Upcoming
          </Typography.Title>
          <List
            dataSource={upcoming}
            locale={{ emptyText: "No upcoming trainings." }}
            renderItem={(t: any) => <TrainingRow t={t} />}
          />
          <Typography.Title level={5} className="!m-0">
            Past
          </Typography.Title>
          <List
            dataSource={past}
            locale={{ emptyText: "No past trainings." }}
            renderItem={(t: any) => <TrainingRow t={t} />}
          />
        </>
      )}
    </Col>
  );
}

function TrainingRow({ t }: { t: any }) {
  const statusColor =
    t.status === "Completed"
      ? "green"
      : t.status === "Cancelled"
        ? "red"
        : "blue";
  return (
    <List.Item>
      <List.Item.Meta
        title={
          <Space>
            <FontAwesomeIcon icon={faChalkboardUser} />
            <span>{t.title}</span>
            <Tag color={statusColor}>{t.status}</Tag>
          </Space>
        }
        description={
          <Space direction="vertical" size={2}>
            <Typography.Text type="secondary">
              {dayjs(t.startDate).format("DD MMM YYYY HH:mm")}
              {" → "}
              {dayjs(t.endDate).format("HH:mm")}
            </Typography.Text>
            {t.hostUserName && (
              <Typography.Text type="secondary">
                Host: {t.hostUserName}
              </Typography.Text>
            )}
            {t.location && (
              <Typography.Text type="secondary">At: {t.location}</Typography.Text>
            )}
          </Space>
        }
      />
    </List.Item>
  );
}

export default WellBeingPage;
